import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Typography, IconButton
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import { i18n } from "../../translate/i18n";

const UNITS = [
  { key: "sec",  label: "Segundos", mult: 1,    icon: "⏱" },
  { key: "min",  label: "Minutos",  mult: 60,   icon: "⏰" },
  { key: "hour", label: "Horas",    mult: 3600, icon: "🕐" },
  { key: "day",  label: "Dias",     mult: 86400,icon: "📅" },
];

const useStyles = makeStyles(() => ({
  dialogPaper: {
    borderRadius: 16,
    border: "1px solid rgba(17, 24, 39, 0.08)",
    boxShadow: "0 20px 60px rgba(16, 24, 40, 0.18)",
    overflow: "hidden",
    maxWidth: 460,
    width: "100%",
  },
  header: {
    padding: "20px 20px 16px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    borderBottom: "1px solid rgba(17, 24, 39, 0.07)",
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    marginBottom: 8,
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: "#111827",
    fontFamily: "Inter, system-ui, sans-serif",
  },
  subtitle: {
    fontSize: 12.5,
    color: "#6B7280",
    marginTop: 2,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "1px solid rgba(17, 24, 39, 0.08)",
    padding: 4,
    flexShrink: 0,
  },
  content: {
    padding: "20px",
    background: "#fafafa",
  },
  inputRow: {
    display: "flex",
    gap: 10,
    alignItems: "stretch",
  },
  valueInput: {
    flex: 1,
    height: 52,
    border: "1.5px solid rgba(17, 24, 39, 0.12)",
    borderRadius: 12,
    background: "#fff",
    padding: "0 14px",
    fontSize: 22,
    fontWeight: 700,
    color: "#111827",
    outline: "none",
    fontFamily: "Inter, system-ui, sans-serif",
    textAlign: "center",
    "&:focus": {
      borderColor: "#f59e0b",
      boxShadow: "0 0 0 3px rgba(245, 158, 11, 0.15)",
    },
  },
  unitsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
    marginTop: 12,
  },
  unitBtn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    padding: "12px 8px",
    borderRadius: 12,
    border: "1.5px solid rgba(17, 24, 39, 0.10)",
    background: "#fff",
    cursor: "pointer",
    transition: "all 0.15s ease",
    fontFamily: "Inter, system-ui, sans-serif",
  },
  unitBtnActive: {
    border: "1.5px solid #f59e0b",
    background: "rgba(245, 158, 11, 0.06)",
    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.15)",
  },
  unitEmoji: {
    fontSize: 20,
    lineHeight: 1,
  },
  unitLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#374151",
  },
  unitLabelActive: {
    color: "#d97706",
  },
  previewBox: {
    marginTop: 16,
    padding: "10px 14px",
    borderRadius: 10,
    background: "rgba(245, 158, 11, 0.08)",
    border: "1px solid rgba(245, 158, 11, 0.25)",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  previewText: {
    fontSize: 13,
    fontWeight: 500,
    color: "#92400e",
  },
  actions: {
    padding: "14px 20px",
    borderTop: "1px solid rgba(17, 24, 39, 0.06)",
    gap: 10,
    justifyContent: "flex-end",
  },
  cancelBtn: {
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 500,
    padding: "8px 20px",
  },
  saveBtn: {
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 600,
    padding: "8px 24px",
    boxShadow: "0 4px 14px rgba(245, 158, 11, 0.35)",
    background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    fontFamily: "Inter, system-ui, sans-serif",
    "&:hover": {
      background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
    },
  },
}));

const FlowBuilderIntervalModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [value, setValue] = useState(1);
  const [unit, setUnit] = useState("min");
  const [activeModal, setActiveModal] = useState(false);

  useEffect(() => {
    if (open === "edit") {
      // Parse existing sec back to friendly display
      const totalSec = data?.data?.sec || 0;
      const existingUnit = data?.data?.unit || "sec";
      const existingValue = data?.data?.value || totalSec;
      setValue(existingValue);
      setUnit(existingUnit);
      setActiveModal(true);
    } else if (open === "create") {
      setValue(1);
      setUnit("min");
      setActiveModal(true);
    }
    return () => { isMounted.current = false; };
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const getUnitInfo = (key) => UNITS.find(u => u.key === key);

  const totalSeconds = () => {
    const u = getUnitInfo(unit);
    return parseInt(value || 0) * (u ? u.mult : 1);
  };

  const previewLabel = () => {
    const v = parseInt(value || 0);
    if (!v) return "0 segundos";
    const u = getUnitInfo(unit);
    return `${v} ${u ? u.label.toLowerCase() : ""}`;
  };

  const handleSave = () => {
    const v = parseInt(value);
    if (!v || v <= 0) {
      return toast.error("Informe um valor maior que zero");
    }

    const payload = {
      sec: totalSeconds(),
      value: v,
      unit: unit,
    };

    if (open === "edit") {
      onUpdate({ ...data, data: payload });
    } else {
      onSave(payload);
    }
    handleClose();
  };

  return (
    <Dialog
      open={activeModal}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      {/* Header */}
      <div className={classes.header}>
        <div>
          <div className={classes.headerIcon}>⏰</div>
          <Typography className={classes.title}>
            {open === "create" ? "Adicionar tempo de espera" : "Editar tempo de espera"}
          </Typography>
          <Typography className={classes.subtitle}>
            O fluxo aguarda antes de continuar para o próximo passo
          </Typography>
        </div>
        <IconButton onClick={handleClose} className={classes.closeBtn} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* Content */}
      <DialogContent className={classes.content}>
        {/* Value input */}
        <div style={{ marginBottom: 12 }}>
          <Typography style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
            Quantidade
          </Typography>
          <input
            type="number"
            min="1"
            value={value}
            onChange={e => setValue(e.target.value)}
            style={{
              width: "100%",
              height: 56,
              border: "1.5px solid rgba(17, 24, 39, 0.12)",
              borderRadius: 12,
              background: "#fff",
              padding: "0 16px",
              fontSize: 24,
              fontWeight: 700,
              color: "#111827",
              outline: "none",
              fontFamily: "Inter, system-ui, sans-serif",
              textAlign: "center",
              boxSizing: "border-box",
            }}
            onFocus={e => { e.target.style.borderColor = "#f59e0b"; e.target.style.boxShadow = "0 0 0 3px rgba(245,158,11,0.12)"; }}
            onBlur={e => { e.target.style.borderColor = "rgba(17,24,39,0.12)"; e.target.style.boxShadow = "none"; }}
          />
        </div>

        {/* Unit grid */}
        <Typography style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
          Unidade
        </Typography>
        <div className={classes.unitsGrid}>
          {UNITS.map(u => (
            <button
              key={u.key}
              type="button"
              className={`${classes.unitBtn} ${unit === u.key ? classes.unitBtnActive : ""}`}
              onClick={() => setUnit(u.key)}
              style={{
                border: unit === u.key ? "1.5px solid #f59e0b" : "1.5px solid rgba(17, 24, 39, 0.10)",
                background: unit === u.key ? "rgba(245, 158, 11, 0.06)" : "#fff",
                boxShadow: unit === u.key ? "0 2px 8px rgba(245, 158, 11, 0.15)" : "none",
              }}
            >
              <span className={classes.unitEmoji}>{u.icon}</span>
              <span className={classes.unitLabel} style={{ color: unit === u.key ? "#d97706" : "#374151" }}>
                {u.label}
              </span>
            </button>
          ))}
        </div>

        {/* Preview */}
        <div className={classes.previewBox}>
          <span style={{ fontSize: 16 }}>⏳</span>
          <Typography className={classes.previewText}>
            Aguardar <strong>{previewLabel()}</strong> antes de continuar
          </Typography>
        </div>
      </DialogContent>

      {/* Actions */}
      <DialogActions className={classes.actions}>
        <Button onClick={handleClose} variant="outlined" className={classes.cancelBtn}>
          Cancelar
        </Button>
        <button onClick={handleSave} className={classes.saveBtn}>
          {open === "create" ? "Adicionar" : "Salvar"}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderIntervalModal;
